/**
 * SceneInfoCard (web) — Live Vision universal-lens result. Composes the
 * established theme primitives (Card / Text / Icon) — no bespoke surfaces.
 */
import { useState } from 'react';
import Card from '../../common/Card';
import Text from '../../common/Text';
import Icon from '../../common/Icon';
import { useLanguage } from '../../hooks/useLanguage';

const CATEGORY_ICON: Record<string, string> = {
  landmark: 'business-outline',
  sign: 'language-outline',
  food_dish: 'restaurant-outline',
  nature: 'leaf-outline',
  product: 'pricetag-outline',
  other: 'eye-outline',
};

export default function SceneInfoCard({ item }: { item: any }) {
  const { t } = useLanguage();
  const card = item?.summary !== undefined ? item : (item?.data ?? item ?? {});
  const category: string = card.category || 'other';
  const details: string[] = (card.details || []).filter(Boolean).slice(0, 5);
  const icon = CATEGORY_ICON[category] || 'eye-outline';

  // Keep the at-a-glance view brief (header + summary); everything else folds
  // behind a More toggle so a long listing doesn't dominate the feed.
  const hasMore =
    !!card.translation ||
    details.length > 0 ||
    !!card.local_context ||
    !!card.cautions ||
    !!card.attribution?.text;
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <div style={HEADER}>
        <Icon name={icon} size={18} color="#fff" />
        <div style={{ flex: 1 }}>
          <Text variant="h3">{card.title}</Text>
          {!!card.subtitle && <Text variant="caption">{card.subtitle}</Text>}
        </div>
        <Text variant="pill">{String(category).replace('_', ' ')}</Text>
      </div>

      {!!card.summary && <Text variant="body">{card.summary}</Text>}

      {expanded && (
        <>
          {!!card.translation && (
            <div style={{ marginTop: 8 }}>
              <Text variant="label">{t('SCENE_TRANSLATION_LABEL')}</Text>
              <Text variant="body">{card.translation}</Text>
            </div>
          )}

          {details.length > 0 && (
            <div>
              {details.map((d, i) => (
                <Text key={i} variant="label">
                  • {d}
                </Text>
              ))}
            </div>
          )}

          {!!card.local_context && (
            <div style={NOTE}>
              <Icon name="location-outline" size={14} color="#fff" />
              <Text variant="label">{card.local_context}</Text>
            </div>
          )}
          {!!card.cautions && (
            <div style={NOTE}>
              <Icon name="warning-outline" size={14} color="#fff" />
              <Text variant="label">{card.cautions}</Text>
            </div>
          )}
          {!!card.attribution?.text && (
            <Text variant="caption">{card.attribution.text}</Text>
          )}
        </>
      )}

      {hasMore && (
        <button
          type="button"
          style={MORE_BTN}
          onClick={() => setExpanded(e => !e)}
          aria-label={
            expanded ? t('COLLAPSE', 'Collapse') : t('EXPAND', 'Expand')
          }
        >
          <Icon
            name={
              expanded ? 'chevron-collapse-outline' : 'chevron-expand-outline'
            }
            size={20}
            color="#aa12ff"
          />
        </button>
      )}
    </Card>
  );
}

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
};
const NOTE: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'flex-start',
  marginTop: 8,
};
const MORE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 10,
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};
